import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Calendar, Users, Video, Dumbbell, Star, Clock, MapPin, 
  Phone, Mail, Instagram, ChevronRight, Play, Trophy, Heart,
  LogOut, User, ArrowRight, Check, Loader2, Target, Flame, Zap,
  Brain, ClipboardCheck, Shield, CheckCircle2, ChevronDown, MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Jeff's real photo
const JEFF_PHOTO = "https://customer-assets.emergentagent.com/job_fix-my-site-7/artifacts/b8p7or2d_IMG_6232.jpeg";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fix-my-site-7/artifacts/0yryqon5_0DA7F1E5-C0B7-4771-834C-B277B56184EE.png";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      
      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        setUser(response.data);
        toast.success("Welcome back!");
        navigate('/dashboard', { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate('/');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 spinner mx-auto mb-4"></div>
        <p className="text-zinc-400">Authenticating...</p>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, login, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "The Method", path: "/method" },
    { name: "Pricing", path: "/pricing" },
    { name: "Results", path: "/results" },
    { name: "Apply Now", path: "/apply" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass border-b border-zinc-800/50' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="The Jeff's Way Method" className="h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  location.pathname === link.path ? 'text-[#E11D48]' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name.toUpperCase()}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="dashboard-btn">
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
                <Button variant="ghost" onClick={logout} className="text-zinc-400 hover:text-white" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button 
                onClick={login} 
                className="bg-[#E11D48] hover:bg-[#BE123C] text-white primary-glow btn-scale"
                data-testid="login-btn"
              >
                Sign In
              </Button>
            )}
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden text-white" data-testid="mobile-menu-btn">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-zinc-800"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block text-lg font-medium ${
                    location.pathname === link.path ? 'text-[#E11D48]' : 'text-zinc-400'
                  }`}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsOpen(false)} className="block text-lg font-medium text-zinc-400">
                    DASHBOARD
                  </Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="block text-lg font-medium text-zinc-400">
                    LOGOUT
                  </button>
                </>
              ) : (
                <button onClick={login} className="block text-lg font-medium text-[#E11D48]">
                  SIGN IN
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-[#0a0a0c] border-t border-zinc-800 py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="col-span-1 md:col-span-2">
          <img src={LOGO_URL} alt="The Jeff's Way Method" className="h-16 w-auto mb-4" />
          <p className="text-zinc-400 max-w-md mb-2">
            Online coaching for men over 30 who are done restarting. 12-week discipline-first body transformation.
          </p>
          <p className="text-zinc-500 italic text-sm">Discipline Over Motivation</p>
          <div className="flex gap-4 mt-6">
            <a href="https://www.instagram.com/fitfight_coachjeff" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-[#E11D48] transition-colors">
              <Instagram className="w-6 h-6" />
            </a>
            <a href="https://wa.me/447444689629" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-[#25D366] transition-colors">
              <MessageCircle className="w-6 h-6" />
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Quick Links</h4>
          <div className="space-y-2">
            <Link to="/method" className="block text-zinc-400 hover:text-white transition-colors">The Method</Link>
            <Link to="/pricing" className="block text-zinc-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/results" className="block text-zinc-400 hover:text-white transition-colors">Results</Link>
            <Link to="/apply" className="block text-zinc-400 hover:text-white transition-colors">Apply Now</Link>
          </div>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Contact</h4>
          <div className="space-y-3 text-zinc-400">
            <p className="flex items-center gap-2"><Video className="w-4 h-4 text-[#E11D48]" /> 100% Online Coaching</p>
            <a href="https://wa.me/447444689629" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366]"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Jeff</a>
            <a href="https://www.instagram.com/fitfight_coachjeff" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#E11D48]"><Instagram className="w-4 h-4 text-[#E11D48]" /> @fitfight_coachjeff</a>
          </div>
        </div>
      </div>
      <div className="border-t border-zinc-800 mt-12 pt-8 text-center text-zinc-500">
        <p>&copy; {new Date().getFullYear()} The Jeff's Way Method™. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Hero Section - Updated for program
const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#09090B]" />
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <img src={LOGO_URL} alt="The Jeff's Way Method" className="h-28 md:h-36 w-auto mx-auto mb-8" />
          
          <div className="inline-block bg-[#E11D48]/20 border border-[#E11D48]/40 rounded-full px-4 py-2 mb-6">
            <span className="text-[#E11D48] text-sm font-medium tracking-wide">12-WEEK TRANSFORMATION PROGRAM</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 tracking-tighter leading-tight">
            FOR MEN OVER 30<br />WHO ARE <span className="text-[#E11D48]">DONE RESTARTING</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-zinc-400 mb-4 max-w-3xl mx-auto">
            This is not motivation-based coaching.
          </p>
          <p className="text-lg md:text-xl text-zinc-300 mb-10 max-w-3xl mx-auto font-medium">
            This is structured implementation built on non-negotiable standards.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white text-lg px-10 py-7 primary-glow btn-scale font-bold tracking-wide"
              onClick={() => navigate('/apply')}
              data-testid="hero-apply-btn"
            >
              APPLY NOW <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 text-lg px-10 py-7"
              onClick={() => navigate('/method')}
              data-testid="hero-method-btn"
            >
              SEE THE METHOD
            </Button>
          </div>

          <p className="text-zinc-500 mt-8 text-sm">
            Application required. Limited spots available.
          </p>
        </motion.div>
      </div>

      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ChevronDown className="w-8 h-8 text-white/50" />
      </motion.div>
    </section>
  );
};

// Problem Statement Section
const ProblemSection = () => (
  <section className="py-24 bg-[#09090B]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-black text-white mb-8">
          YOU'VE TRIED BEFORE. <span className="text-[#E11D48]">MULTIPLE TIMES.</span>
        </h2>
        <div className="space-y-4 text-zinc-400 text-lg">
          <p>You've downloaded the apps. Bought the programs. Started strong on Monday.</p>
          <p>By Thursday? Life happened. Motivation disappeared.</p>
          <p className="text-white font-medium pt-4">The problem was never information. It was implementation.</p>
        </div>
        <div className="mt-12 p-6 bg-zinc-900/50 border border-zinc-800 rounded-lg">
          <p className="text-[#E11D48] text-xl font-bold italic">"Discipline Over Motivation"</p>
          <p className="text-zinc-500 mt-2">— The Jeff's Way Philosophy</p>
        </div>
      </motion.div>
    </div>
  </section>
);

// Pillars Section
const PillarsSection = () => {
  const pillars = [
    { icon: Target, name: "The Discipline Blueprint", desc: "Build unshakeable daily systems that eliminate decision fatigue and create automatic execution." },
    { icon: Flame, name: "Metabolic Reconstruction Protocol", desc: "Strategic nutrition and training protocols designed specifically for the 30+ male metabolism." },
    { icon: Zap, name: "The 30+ Performance System", desc: "Age-optimized training methodology that builds strength, mobility, and functional power." },
    { icon: Brain, name: "Identity Realignment Framework", desc: "Transform how you see yourself. Become the man who does what he says he'll do." },
    { icon: ClipboardCheck, name: "Weekly Accountability & Execution Tracking", desc: "Direct access to Jeff with weekly check-ins, progress tracking, and course corrections." },
  ];

  return (
    <section className="py-24 bg-[#0f0f11]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="accent-line mx-auto mb-4" />
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">INSIDE THE METHOD</h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Five integrated systems that work together to create permanent transformation
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}
              >
                <Card className="bg-[#18181B] border-zinc-800 card-hover p-6 h-full" data-testid={`pillar-${index}`}>
                  <div className="w-14 h-14 bg-[#E11D48]/20 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#E11D48]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{pillar.name}</h3>
                  <p className="text-zinc-400">{pillar.desc}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Who It's For Section
const WhoItsForSection = () => {
  const navigate = useNavigate();
  
  const forYouIf = [
    "You're over 30 and feel like your body doesn't respond like it used to",
    "You've 'started over' more times than you can count",
    "You're tired of programs that require motivation you don't have",
    "You're ready to operate on standards — not feelings",
    "You want a system, not another app or PDF",
  ];

  const notForYouIf = [
    "You're looking for a quick fix or magic pill",
    "You're not willing to commit for 12 weeks minimum",
    "You need constant motivation and hand-holding",
    "You're not ready to invest in yourself",
  ];

  return (
    <section className="py-24 bg-[#09090B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="accent-line mx-auto mb-4" />
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">IS THIS FOR YOU?</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-[#18181B] border-zinc-800 p-8 h-full">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
                <h3 className="text-2xl font-bold text-white">THIS IS FOR YOU IF:</h3>
              </div>
              <ul className="space-y-4">
                {forYouIf.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-[#18181B] border-zinc-800 p-8 h-full">
              <div className="flex items-center gap-3 mb-6">
                <X className="w-8 h-8 text-red-500" />
                <h3 className="text-2xl font-bold text-white">THIS IS NOT FOR YOU IF:</h3>
              </div>
              <ul className="space-y-4">
                {notForYouIf.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button 
            size="lg" 
            className="bg-[#E11D48] hover:bg-[#BE123C] text-white text-lg px-10 py-7 primary-glow btn-scale font-bold"
            onClick={() => navigate('/apply')}
            data-testid="cta-apply-btn"
          >
            APPLY FOR THE PROGRAM <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Book Section
const BookSection = () => (
  <section className="py-24 bg-[#0f0f11]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <div className="relative">
            <img 
              src="https://m.media-amazon.com/images/I/616hvsEM71L._SY466_.jpg" 
              alt="Mind Over Muscle Book" 
              className="rounded-lg shadow-2xl max-w-xs"
            />
            <div className="absolute -bottom-4 -right-4 bg-[#E11D48] text-white px-4 py-2 rounded-lg font-bold">
              NEW RELEASE
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="accent-line mb-4" />
          <h2 className="text-3xl md:text-4xl font-black text-white mb-2">THE BOOK</h2>
          <h3 className="text-2xl md:text-3xl font-bold text-[#E11D48] mb-6">Mind Over Muscle</h3>
          <p className="text-zinc-300 text-lg mb-4 italic">
            "Before You Ask for Help, Ask Yourself"
          </p>
          <p className="text-zinc-400 mb-6 leading-relaxed">
            True transformation originates not from external factors like diet plans or gym routines, but from an internal, mental foundation. The real battleground for fitness lies within your own mind.
          </p>
          <ul className="space-y-3 mb-8">
            <li className="flex items-start gap-3 text-zinc-300">
              <Check className="w-5 h-5 text-[#E11D48] mt-0.5" />
              Discover your Unshakable "Why"
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <Check className="w-5 h-5 text-[#E11D48] mt-0.5" />
              Rewire your brain: Thoughts become actions
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <Check className="w-5 h-5 text-[#E11D48] mt-0.5" />
              Build unbreakable self-discipline
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <Check className="w-5 h-5 text-[#E11D48] mt-0.5" />
              Master stress management
            </li>
          </ul>
          <a 
            href="https://amzn.to/4c0ZehW" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              size="lg"
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white primary-glow btn-scale"
              data-testid="buy-book-btn"
            >
              GET THE BOOK ON AMAZON <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="text-zinc-500 mt-4 text-sm">Available in Paperback & Hardcover</p>
        </motion.div>
      </div>
    </div>
  </section>
);

// About Jeff Section
const AboutSection = () => (
  <section className="py-24 bg-[#0f0f11]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <img 
            src={JEFF_PHOTO} 
            alt="Jeff Oliveira" 
            className="rounded-lg w-full object-cover max-h-[600px]"
          />
          <div className="absolute -bottom-6 -right-6 bg-[#E11D48] p-6 rounded-lg">
            <Trophy className="w-8 h-8 text-white mb-2" />
            <p className="text-white font-bold">Certified<br />Wellness Coach</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="accent-line mb-4" />
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">MEET JEFF OLIVEIRA</h2>
          <p className="text-zinc-400 mb-6 text-lg leading-relaxed">
            I've been where you are. Multiple restarts. Frustration. Wondering if transformation is even possible after 30.
          </p>
          <p className="text-zinc-400 mb-6 text-lg leading-relaxed">
            After years of trial and error, I discovered the truth: motivation is unreliable. Discipline is the only path to permanent change.
          </p>
          <p className="text-zinc-300 mb-8 text-lg leading-relaxed font-medium">
            The Jeff's Way Method is the system I wish existed when I started. Now I've helped hundreds of men transform not just their bodies, but their entire identity.
          </p>
          <div className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
            <Shield className="w-8 h-8 text-[#E11D48]" />
            <div>
              <p className="text-white font-bold">Founded by Jeff Oliveira</p>
              <p className="text-zinc-400 text-sm italic">"Discipline Over Motivation"</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Reviews Section
const ReviewsSection = ({ reviews }) => (
  <section className="py-24 bg-[#09090B]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <div className="accent-line mx-auto mb-4" />
        <h2 className="text-4xl md:text-5xl font-black text-white mb-4">WHAT MEN ARE SAYING</h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {reviews.map((review, index) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-[#18181B] border-zinc-800 p-6 h-full" data-testid={`review-${index}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#E11D48] text-[#E11D48]" />
                ))}
              </div>
              <p className="text-zinc-300 mb-6 italic">"{review.content}"</p>
              <p className="text-white font-bold">{review.name}</p>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-gradient-to-b from-[#0f0f11] to-[#09090B]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            READY TO STOP RESTARTING?
          </h2>
          <p className="text-xl text-zinc-400 mb-8">
            Submit your application and discover if The Jeff's Way Method is right for you.
          </p>
          <Button 
            size="lg" 
            className="bg-[#E11D48] hover:bg-[#BE123C] text-white text-xl px-12 py-8 primary-glow btn-scale font-bold"
            onClick={() => navigate('/apply')}
            data-testid="final-cta-btn"
          >
            APPLY NOW <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
          <p className="text-zinc-500 mt-6 text-sm">
            Limited spots available. Application required.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(res => setReviews(res.data));
  }, []);

  return (
    <>
      <HeroSection />
      <ProblemSection />
      <PillarsSection />
      <WhoItsForSection />
      <BookSection />
      <AboutSection />
      <ReviewsSection reviews={reviews} />
      <CTASection />
    </>
  );
};

// Method Page
const MethodPage = () => {
  const navigate = useNavigate();
  const pillars = [
    { icon: Target, name: "The Discipline Blueprint", desc: "Build unshakeable daily systems that eliminate decision fatigue and create automatic execution. No more willpower battles. No more 'I'll start Monday.' Just clean, repeatable systems that run on autopilot.", details: ["Morning protocol design", "Non-negotiable standard setting", "Environment optimization", "Decision elimination systems"] },
    { icon: Flame, name: "Metabolic Reconstruction Protocol", desc: "Your metabolism at 35 isn't your metabolism at 25. We work with your biology, not against it. Strategic nutrition timing, hormonal optimization, and protocols designed for the 30+ male body.", details: ["Age-specific nutrition protocols", "Metabolic timing strategies", "Hormone-supportive eating", "Sustainable meal frameworks"] },
    { icon: Zap, name: "The 30+ Performance System", desc: "Train smarter, not just harder. Build real-world strength, protect your joints, and create a body that performs — not just looks good.", details: ["Joint-friendly progressions", "Functional strength building", "Mobility integration", "Recovery protocols"] },
    { icon: Brain, name: "Identity Realignment Framework", desc: "The reason you keep restarting? You still see yourself as someone who quits. We rebuild your identity from the ground up. Become the man who does what he says he'll do.", details: ["Identity reprogramming", "Belief system overhaul", "Self-image reconstruction", "Behavioral psychology application"] },
    { icon: ClipboardCheck, name: "Weekly Accountability & Execution Tracking", desc: "Direct access to Jeff. Weekly check-ins. Real-time course corrections. You're never alone in this process.", details: ["Weekly 1:1 check-ins", "Progress tracking dashboard", "Real-time messaging support", "Strategy adjustments"] },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <div className="accent-line mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">THE METHOD</h1>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
            A 12-week discipline-first body transformation system. Five integrated pillars working together to create permanent change.
          </p>
        </motion.div>

        <div className="space-y-16">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            const isEven = index % 2 === 0;
            
            return (
              <motion.div
                key={pillar.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={!isEven ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-[#E11D48]/20 rounded-lg flex items-center justify-center">
                      <Icon className="w-8 h-8 text-[#E11D48]" />
                    </div>
                    <span className="text-zinc-500 text-sm font-medium">PILLAR {index + 1}</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">{pillar.name}</h3>
                  <p className="text-zinc-400 text-lg mb-6">{pillar.desc}</p>
                  <ul className="space-y-3">
                    {pillar.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3 text-zinc-300">
                        <Check className="w-5 h-5 text-[#E11D48]" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-[#18181B] border border-zinc-800 rounded-lg p-8 ${!isEven ? 'lg:order-1' : ''}`}>
                  <div className="aspect-video bg-zinc-900 rounded-lg flex items-center justify-center">
                    <Icon className="w-24 h-24 text-zinc-700" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <Button 
            size="lg" 
            className="bg-[#E11D48] hover:bg-[#BE123C] text-white text-lg px-10 py-7 primary-glow btn-scale font-bold"
            onClick={() => navigate('/apply')}
            data-testid="method-apply-btn"
          >
            APPLY FOR THE METHOD <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

// Results/Gallery Page
const ResultsPage = () => {
  const [transformations, setTransformations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/gallery`),
      axios.get(`${API}/reviews`)
    ]).then(([galleryRes, reviewsRes]) => {
      setTransformations(galleryRes.data);
      setReviews(reviewsRes.data);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="accent-line mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">REAL RESULTS</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            These are real transformations from men who committed to The Method. No filters. No excuses. Just discipline.
          </p>
        </motion.div>

        {/* Transformation Photos */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">CLIENT TRANSFORMATIONS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {transformations.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[#18181B] border-zinc-800 overflow-hidden card-hover" data-testid={`transformation-${index}`}>
                  <div className="relative">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-auto object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
                      <p className="text-white font-bold">{item.title}</p>
                      <p className="text-zinc-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">WHAT CLIENTS SAY</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[#18181B] border-zinc-800 p-6 h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#E11D48] text-[#E11D48]" />
                    ))}
                  </div>
                  <p className="text-zinc-300 mb-6 italic">"{review.content}"</p>
                  <p className="text-white font-bold">{review.name}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">READY TO BE NEXT?</h3>
            <p className="text-zinc-400 mb-6">
              These results came from discipline, not motivation. Apply now and start your transformation.
            </p>
            <Button 
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white primary-glow btn-scale text-lg px-8 py-6"
              onClick={() => navigate('/apply')}
              data-testid="results-apply-btn"
            >
              APPLY NOW <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Application Page
const ApplyPage = () => {
  const [step, setStep] = useState(0); // Start at 0 for the qualifier
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    current_situation: '',
    goals: '',
    commitment_level: '',
    why_now: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, {
        ...formData,
        age: parseInt(formData.age)
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md mx-auto px-4 text-center"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">APPLICATION RECEIVED</h1>
          <p className="text-zinc-400 mb-8">
            Thank you for applying. If you're a good fit for The Method, you'll hear from us within 48 hours.
          </p>
          <Link to="/">
            <Button className="bg-zinc-800 hover:bg-zinc-700 text-white">
              Return Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Step 0: The Qualifier
  if (step === 0) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <img src={LOGO_URL} alt="The Jeff's Way Method" className="h-24 w-auto mx-auto mb-12" />
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-8 leading-relaxed">
              Welcome to The Jeff's Way Method™.
            </h1>
            
            <p className="text-xl text-zinc-400 mb-12">
              Before we speak —
            </p>
            
            <div className="space-y-4 mb-12">
              <p className="text-2xl md:text-3xl text-white font-medium">
                Are you <span className="text-[#E11D48]">done restarting</span>,
              </p>
              <p className="text-2xl md:text-3xl text-white font-medium">
                or are you looking for motivation?
              </p>
            </div>
            
            <p className="text-zinc-500 italic mb-12">
              Answer honestly.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#E11D48] hover:bg-[#BE123C] text-white text-lg px-8 py-6 primary-glow btn-scale font-bold"
                onClick={() => setStep(1)}
                data-testid="qualifier-done-restarting"
              >
                I'M DONE RESTARTING
              </Button>
              <Link to="/">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-lg px-8 py-6 w-full"
                  data-testid="qualifier-need-motivation"
                >
                  I need motivation
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="accent-line mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">APPLY NOW</h1>
          <p className="text-zinc-400">
            Complete this application to see if The Jeff's Way Method is right for you.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-[#E11D48] text-white' : 'bg-zinc-800 text-zinc-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-20 md:w-32 h-1 ${step > s ? 'bg-[#E11D48]' : 'bg-zinc-800'}`} />}
            </div>
          ))}
        </div>

        <Card className="bg-[#18181B] border-zinc-800 p-8">
          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h3 className="text-xl font-bold text-white mb-4">BASIC INFORMATION</h3>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Full Name *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="Your full name"
                    data-testid="apply-name"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="your@email.com"
                    data-testid="apply-email"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Phone *</label>
                  <Input
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="+44 7..."
                    data-testid="apply-phone"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Age *</label>
                  <Input
                    type="number"
                    required
                    min="18"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="Your age"
                    data-testid="apply-age"
                  />
                </div>
                <Button 
                  type="button"
                  className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white h-12"
                  onClick={() => setStep(2)}
                  disabled={!formData.name || !formData.email || !formData.phone || !formData.age}
                  data-testid="apply-next-1"
                >
                  CONTINUE
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h3 className="text-xl font-bold text-white mb-4">YOUR SITUATION</h3>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Describe your current situation *</label>
                  <Textarea
                    required
                    value={formData.current_situation}
                    onChange={(e) => setFormData({ ...formData, current_situation: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white min-h-[120px]"
                    placeholder="Where are you now with your health and fitness? What have you tried before?"
                    data-testid="apply-situation"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">What are your goals? *</label>
                  <Textarea
                    required
                    value={formData.goals}
                    onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white min-h-[120px]"
                    placeholder="What does success look like for you in 12 weeks?"
                    data-testid="apply-goals"
                  />
                </div>
                <div className="flex gap-4">
                  <Button 
                    type="button"
                    variant="outline"
                    className="flex-1 border-zinc-700 text-white h-12"
                    onClick={() => setStep(1)}
                  >
                    BACK
                  </Button>
                  <Button 
                    type="button"
                    className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white h-12"
                    onClick={() => setStep(3)}
                    disabled={!formData.current_situation || !formData.goals}
                    data-testid="apply-next-2"
                  >
                    CONTINUE
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h3 className="text-xl font-bold text-white mb-4">COMMITMENT</h3>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">How committed are you to making a change? *</label>
                  <Select 
                    value={formData.commitment_level} 
                    onValueChange={(value) => setFormData({ ...formData, commitment_level: value })}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white h-12" data-testid="apply-commitment">
                      <SelectValue placeholder="Select your commitment level" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="exploring">Just exploring options</SelectItem>
                      <SelectItem value="considering">Seriously considering</SelectItem>
                      <SelectItem value="ready">Ready to start immediately</SelectItem>
                      <SelectItem value="all-in">100% committed, nothing will stop me</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Why now? What's changed? *</label>
                  <Textarea
                    required
                    value={formData.why_now}
                    onChange={(e) => setFormData({ ...formData, why_now: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white min-h-[120px]"
                    placeholder="What's making you take action today instead of next month or next year?"
                    data-testid="apply-why-now"
                  />
                </div>
                <div className="flex gap-4">
                  <Button 
                    type="button"
                    variant="outline"
                    className="flex-1 border-zinc-700 text-white h-12"
                    onClick={() => setStep(2)}
                  >
                    BACK
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white h-12"
                    disabled={!formData.commitment_level || !formData.why_now || loading}
                    data-testid="apply-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SUBMIT APPLICATION'}
                  </Button>
                </div>
              </motion.div>
            )}
          </form>
        </Card>

        <p className="text-center text-zinc-500 text-sm mt-6">
          Your information is 100% confidential and will never be shared.
        </p>
      </div>
    </div>
  );
};

// Pricing Page
const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/pricing`).then(res => setPlans(res.data));
  }, []);

  const handleEnroll = async (planId) => {
    if (!user) {
      login();
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(`${API}/enroll`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, { withCredentials: true });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="accent-line mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">PRICING</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Choose the plan that works for you. Same powerful method, flexible payment.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`bg-[#18181B] border-2 ${plan.popular ? 'border-[#E11D48]' : 'border-zinc-800'} p-8 h-full relative`} data-testid={`plan-${plan.id}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#E11D48] text-white text-sm font-bold px-4 py-1 rounded-full">
                    BEST VALUE
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-zinc-400 mb-6">{plan.description}</p>
                
                <div className="mb-6">
                  <span className="text-5xl font-black text-white">£{plan.price}</span>
                  <span className="text-zinc-400 ml-2">/ {plan.duration}</span>
                </div>
                
                {plan.savings && (
                  <div className="bg-green-500/20 text-green-400 text-sm font-medium px-3 py-2 rounded-lg mb-6 inline-block">
                    Save £{plan.savings} vs monthly
                  </div>
                )}
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#E11D48] mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full h-14 text-lg font-bold ${plan.popular ? 'bg-[#E11D48] hover:bg-[#BE123C] primary-glow' : 'bg-zinc-800 hover:bg-zinc-700'} text-white btn-scale`}
                  onClick={() => handleEnroll(plan.id)}
                  disabled={loading === plan.id}
                  data-testid={`enroll-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : user ? (
                    'ENROLL NOW'
                  ) : (
                    'SIGN IN TO ENROLL'
                  )}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-4">NOT SURE YET?</h3>
            <p className="text-zinc-400 mb-6">
              Apply first. We'll have a conversation to see if The Method is right for you before you commit.
            </p>
            <Link to="/apply">
              <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">
                SUBMIT APPLICATION FIRST
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (!sessionId) {
      navigate('/');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };

    pollStatus();
  }, [location.search, navigate]);

  return (
    <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4 text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 spinner mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
            <p className="text-zinc-400">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'success' && (
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">WELCOME TO THE METHOD</h1>
            <p className="text-zinc-400 mb-8">Your enrollment is confirmed. Your transformation starts now.</p>
            <Button 
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              GO TO DASHBOARD
            </Button>
          </motion.div>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Payment Issue</h1>
            <p className="text-zinc-400 mb-8">There was a problem with your payment. Please try again.</p>
            <Button 
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white"
              onClick={() => navigate('/pricing')}
            >
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="accent-line mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">CONTACT</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Have questions about The Method? Get in touch.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="bg-[#18181B] border-zinc-800 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Name</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Email</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Phone (optional)</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white h-12"
                    placeholder="+44 7..."
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Message</label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white min-h-[150px]"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white primary-glow btn-scale h-12"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SEND MESSAGE'}
                </Button>
              </form>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">GET IN TOUCH</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E11D48]/20 rounded-lg flex items-center justify-center">
                    <Video className="w-6 h-6 text-[#E11D48]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Coaching Format</p>
                    <p className="text-zinc-400">100% Online - Train From Anywhere</p>
                  </div>
                </div>
                <a href="https://wa.me/447444689629" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-[#25D366]/20 rounded-lg flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">WhatsApp</p>
                    <p className="text-zinc-400 group-hover:text-[#25D366]">Message Jeff directly</p>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E11D48]/20 rounded-lg flex items-center justify-center">
                    <Instagram className="w-6 h-6 text-[#E11D48]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Instagram</p>
                    <a href="https://www.instagram.com/fitfight_coachjeff" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-[#E11D48]">@fitfight_coachjeff</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
              <h4 className="text-white font-bold mb-2">Ready to Apply?</h4>
              <p className="text-zinc-400 text-sm mb-4">
                Skip the contact form and apply directly for The Method.
              </p>
              <Link to="/apply">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white w-full">
                  APPLY NOW <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2">
            WELCOME, {user.name?.split(' ')[0]?.toUpperCase()}
          </h1>
          <p className="text-zinc-400">Your transformation journey starts here.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="bg-[#18181B] border-zinc-800 p-6" data-testid="user-profile-card">
            <div className="flex items-center gap-4 mb-6">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 bg-[#E11D48] rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-white font-bold">{user.name}</h3>
                <p className="text-zinc-400 text-sm">{user.email}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-[#18181B] border-zinc-800 p-6">
            <h3 className="text-white font-bold mb-4">APPLICATION STATUS</h3>
            <p className="text-zinc-400 mb-4">Check your application status or apply for The Method.</p>
            <Link to="/apply">
              <Button className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white">
                APPLY NOW
              </Button>
            </Link>
          </Card>

          <Card className="bg-[#18181B] border-zinc-800 p-6">
            <h3 className="text-white font-bold mb-4">NEED HELP?</h3>
            <p className="text-zinc-400 mb-4">Have questions? Get in touch with Jeff directly.</p>
            <Link to="/contact">
              <Button variant="outline" className="w-full border-zinc-700 text-white hover:bg-zinc-800">
                CONTACT US
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/');
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  return user || location.state?.user ? children : null;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navigation />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/method" element={<MethodPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/results" element={<ResultsPage />} />
        <Route path="/apply" element={<ApplyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
